import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"

import { StaticQuery, graphql } from "gatsby"

import seoImage from '../images/logo.png';
import gloriaImg from '../images/gloria-diaz-backyard-focus.jpg';

const config = {
    title: 'Gloria Diaz - Brief Therapy in San Francisco',
    twitter: 'https://twitter.com/IdeasyAcciones',
    url: 'https://www.sanfranciscobrieftherapy.com/',
    logo: 'https://www.sanfranciscobrieftherapy.com/static/logo-dd12f293c3829f52ccae1c7d8d354ab7.png',
    gloriaImg: gloriaImg,
};

const getSchemaOrgJSONLD = ({
  isBlogPost,
  url,
  title,
  image,
  description,
  datePublished,
}) => {
  const schemaOrgJSONLD = [
    {
      '@context': 'http://schema.org',
      '@type': 'MedicalBusiness',
      '@id': 'https://www.google.com/maps/place/Gloria+Diaz,+MFT/@37.7607121,-122.4215075,21z/data=!4m13!1m7!3m6!1s0x808f7e3d2281fe51:0xd7501beae842ca96!2s766+Valencia+St,+San+Francisco,+CA+94110!3b1!8m2!3d37.7606847!4d-122.4218286!3m4!1s0x808f7fb9aa857739:0x3a58442dc662d98!8m2!3d37.7606847!4d-122.4218286',
      url,
      name: title,
      image: config.gloriaImg,
      alternateName: config.title,
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        'streetAddress': '766 Valencia St. suite 21',
        'addressLocality': 'San Francisco',
        'addressRegion': 'CA',
        'postalCode': '94110',
        'addressCountry': 'US'
      },
      'telephone': '+14154898816',
    },
  ];

  return isBlogPost
    ? [
        ...schemaOrgJSONLD,
        {
          '@context': 'https://www.sanfranciscobrieftherapy.com/',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': url,
                name: title,
                image,
              },
            },
          ],
        },
        {
          '@context': 'https://www.sanfranciscobrieftherapy.com/',
          '@type': 'BlogPosting',
          url,
          name: title,
          alternateName: config.title,
          headline: title,
          image: {
            '@type': 'ImageObject',
            url: image,
          },
          description,
          author: {
            '@type': 'Person',
            name: 'Gloria Diaz',
          },
          publisher: {
            '@type': 'Organization',
            url: 'https://www.sanfranciscobrieftherapy.com/',
            logo: config.logo,
            name: 'Gloria Diaz',
          },
          mainEntityOfPage: {
            '@type': 'WebSite',
            '@id': config.url,
          },
          datePublished,
        },
      ]
    : schemaOrgJSONLD;
};

function SEO({ description, lang, meta, keywords, title, isBlogPost}) {
  return (
    <StaticQuery
      query={detailsQuery}
      render={data => {
        const metaDescription =
          description || data.site.siteMetadata.description;
        const baseUrl = data.site.siteMetadata.baseUrl;
        const image = `${baseUrl}${seoImage}`;
        const datePublished = isBlogPost ? meta.date : false;
        const url = meta.slug
          ? `${config.url}${meta.slug}`
          : config.url;
        const schemaOrgJSONLD = getSchemaOrgJSONLD({
            isBlogPost,
            url,
            title,
            image,
            description: metaDescription,
            datePublished,
        });

        return (
          <Helmet
            htmlAttributes={{
              lang,
            }}
            title={title}
            titleTemplate={`%s | ${data.site.siteMetadata.title}`}
            meta={[
              {
                name: `description`,
                content: metaDescription,
              },
              {
                property: `og:title`,
                content: title,
              },
              {
                property: `og:description`,
                content: metaDescription,
              },
              {
                property: `og:type`,
                content: `website`,
              },
              {
                property: 'og:image',
                content: image,
              },
              {
                name: 'twitter:image',
                content: image,
              },
              {
                name: `twitter:card`,
                content: `summary`,
              },
              {
                name: `twitter:creator`,
                content: data.site.siteMetadata.author,
              },
              {
                name: `twitter:title`,
                content: title,
              },
              {
                name: `twitter:description`,
                content: metaDescription,
              },
            ]
              .concat(
                keywords.length > 0
                  ? {
                      name: `keywords`,
                      content: keywords.join(`, `),
                    }
                  : []
              )
              .concat(meta)}
            >
              {/* Schema.org tags */}
                <script type="application/ld+json">
                  {JSON.stringify(schemaOrgJSONLD)}
                </script>
            </Helmet>

        )
      }}
    />
  )
}

SEO.defaultProps = {
    lang: `en`,
    meta: [],
    keywords: [`brief therapy`, `San Francisco`, `Psychotherapy`, `MFT`],
    description: 'We will focus on the here and now and find solutions, rediscover your resources, and move you forward in life.',
    image: '',
    pathname: null,
    isBlogPost: false,
};

SEO.propTypes = {
    isBlogPost: PropTypes.bool,
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.array,
    keywords: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string.isRequired,
};

export default SEO;

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        title
        description
        author
        baseUrl
      }
    }
  }
`;
